import { cn } from '@/lib/utils';
import { LoadingPage } from '@chiroup/components/LoadingPage';
import { convertToMS } from '@chiroup/core/functions/convertToMS';
import { Appointment } from '@chiroup/core/types/Appointment.type';
import { HealthComLocation } from '@chiroup/core/types/HealthComLocation.type';
import { useWindowDimensions } from '@chiroup/hooks/useWindowDimensions';
import dayjs, { Dayjs } from 'dayjs';
import { Calendar as CalendarIcon } from 'lucide-react';
import React, { useCallback, useState } from 'react';
import appointmentService from '../../services/appointment.service';
import locationService from '../../services/location.service';
import ScheduleTime from '../ScheduleTime';
import Modal from '../common/Modal';

type Props = {
  updateAppointment: (id: string, data: Partial<Appointment>) => void;
  provider: string;
  treatment: number;
  id: string;
  location: HealthComLocation;
  primaryColor: string;
  startTime: string | number;
  startTimeFormatted?: string;
  cancel: () => void;
  path: string;
};

const ChangeAppointmentButton: React.FC<Props> = ({
  updateAppointment,
  provider,
  treatment,
  id,
  location,
  primaryColor,
  startTime,
  startTimeFormatted,
  cancel,
  path,
}) => {
  const { height, width } = useWindowDimensions();
  const [selectedDate, setSelectedDate] = useState<Dayjs>();
  const [viewDate, setViewDate] = useState(dayjs(convertToMS(startTime)).startOf('month'));
  const [isOpening, setIsOpening] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [availability, setAvailability] = useState<{
    [key: string]: {
      [key: string]: {
        date: string;
        startTime: Dayjs;
        endTime: Dayjs;
        provider: string;
        duration: number;
      }[];
    };
  }>();
  const [dottedDays, setDottedDays] = useState<{
    [key: number]: boolean;
  }>({});
  const [availableTimes, setAvailableTimes] = useState<
    {
      providerId: string;
      providerName?: string;
      profileImage?: string | null;
      times: {
        date: string;
        startTime: Dayjs;
        endTime: Dayjs;
        provider: string;
        duration: number;
      }[];
    }[]
  >([]);
  const [selectedDay, setSelectedDay] = useState<string>();
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<{
    date: string;
    startTime: Dayjs;
    endTime: Dayjs;
    provider: string;
    duration: number;
  } | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const isSmallViewport = width < 768 || height < 1024;

  const close = () => {
    setAvailableTimes([]);
    setSelectedTimeSlot(null);
    setError(null);
    setIsOpen(false);
  };

  const providerInfo = useCallback(
    (userId: string) => {
      return location?.providers?.find(p => p.ID === userId);
    },
    [location?.providers]
  );

  const onSelectDate = useCallback(
    (
      date: string,
      avail:
        | {
            [key: string]: {
              [key: string]: {
                date: string;
                startTime: Dayjs;
                endTime: Dayjs;
                provider: string;
                duration: number;
              }[];
            };
          }
        | undefined = availability
    ) => {
      if (!date) return setAvailableTimes([]);
      const availabilityForDay = avail?.[date];
      const availabilityForProvider = [
        {
          providerId: provider,
          providerName: providerInfo(provider)?.name,
          profileImage: providerInfo(provider)?.profileImage,
          times: availabilityForDay?.[provider] || [],
        },
      ];
      setAvailableTimes(availabilityForProvider);
      setSelectedDay(dayjs(date).format('MMMM D, YYYY'));
    },
    [provider, providerInfo, availability]
  );

  // Strip JSON-stringified wrapping quotes the backend sometimes
  // emits around error messages (e.g. `\"Invalid provider...\"`).
  const cleanErrorMessage = (raw: unknown): string => {
    if (typeof raw !== 'string' || !raw) {
      return 'This appointment can no longer be changed online. Please contact the clinic to reschedule.';
    }

    return raw.replace(/^"|"$/g, '');
  };

  const getAvailability = async (
    provider: string | null,
    treatment: number | null,
    year: string,
    month: string
  ) => {
    setLoadingAvailability(true);
    setError(null);

    try {
      const res = await locationService.getAvailability({
        path,
        provider,
        treatment,
        year,
        month,
        timezone: location.timezone,
      });

      setAvailability(res.formattedAvailability);
      setDottedDays(
        Object.entries(res.formattedAvailability).reduce(
          (
            acc: {
              [key: number]: boolean;
            },
            [date, dayAvailability]
          ) => {
            const day = dayjs(date).date();
            const isAvailable = Object.values(dayAvailability).some(times => times.length);

            if (isAvailable) {
              acc[day] = true;
            }

            return acc;
          },
          {}
        )
      );

      return res;
    } catch (err: any) {
      // Most common reason this fires post-booking: the treatment was
      // removed from the provider since the appointment was created.
      // Surface the backend's message so the patient knows to call —
      // CUP-side schedulers are unconstrained by the same validation,
      // so the appointment is still valid; it just can't be rescheduled
      // through self-service until the clinic restores the treatment.
      setError(cleanErrorMessage(err?.response?.data?.message));
      setAvailability(undefined);
      setDottedDays({});

      return null;
    } finally {
      setLoadingAvailability(false);
    }
  };

  const openModal = async () => {
    setIsOpening(true);

    try {
      const startTimeDayjs = dayjs(convertToMS(startTime));
      setSelectedDate(startTimeDayjs);

      const res = await getAvailability(
        provider,
        treatment,
        viewDate.format('YYYY'),
        viewDate.format('MM')
      );

      // Open the modal either way — on failure, `getAvailability` has
      // set `error` so the inline Alert renders the message instead
      // of leaving the user staring at a stuck "Loading..." button.
      setIsOpen(true);

      if (res) {
        onSelectDate(startTimeDayjs.format('YYYY-MM-DD'), res.formattedAvailability);
      } else {
        setAvailableTimes([]);
      }
    } finally {
      setIsOpening(false);
    }
  };

  const closeAndCancel = () => {
    close();
    cancel();
  };

  const reschedule = async (val: {
    date: string;
    startTime: Dayjs;
    endTime: Dayjs;
    provider: string;
    duration: number;
  }) => {
    try {
      setError(null);
      setIsSaving(true);
      const res = await appointmentService.update(id, val);
      close();
      updateAppointment(id, res);
    } catch (err: any) {
      setError(cleanErrorMessage(err?.response?.data?.message));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        disabled={isOpening}
        className="inline-flex items-center rounded-xl px-4 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-200 dark:ring-white/10 text-gray-700 dark:text-gray-300 transition-colors active:bg-gray-50 dark:active:bg-white/5"
      >
        {isOpening ? 'Loading...' : 'Change'}
      </button>
      <Modal
        isOpen={isOpen}
        close={close}
        size={isSmallViewport ? 'full' : 'default'}
        className={cn(isSmallViewport ? 'py-4 overflow-y-auto overflow-x-hidden' : '')}
        showCloseButton
      >
        {isSaving ? (
          <LoadingPage />
        ) : (
          <>
            <ScheduleTime
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              onChange={val => setSelectedTimeSlot(val)}
              selectedTimeSlot={selectedTimeSlot}
              primaryColor={primaryColor}
              onSelectDate={onSelectDate}
              selectedDay={selectedDay}
              onChangeMonth={(year: string, month: string) => {
                getAvailability(provider, treatment, year, month);
              }}
              dottedDays={dottedDays}
              loading={loadingAvailability}
              availableTimes={availableTimes}
              setViewDate={setViewDate}
              viewDate={viewDate}
              title={
                <div>
                  <div
                    className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl text-white"
                    style={{ backgroundColor: primaryColor || 'var(--primary-color)' }}
                  >
                    <CalendarIcon className="h-7 w-7" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:mt-5">
                    <h3 className="text-lg font-bold text-gray-900">Change Appointment</h3>
                    <p className="mt-2 text-sm text-gray-400">
                      Select a new date and time to reschedule your {startTimeFormatted} appointment
                      with {providerInfo(provider)?.name}.
                    </p>
                  </div>
                </div>
              }
              error={error}
              className={cn('relative')}
              compact
            />
            <div className="mt-6 flex flex-col gap-3">
              <button
                type="button"
                onClick={() => selectedTimeSlot && reschedule(selectedTimeSlot)}
                disabled={!selectedTimeSlot}
                className="w-full rounded-xl px-4 py-3 text-sm font-semibold text-white transition-opacity disabled:opacity-50 active:opacity-80"
                style={{ backgroundColor: primaryColor || 'var(--primary-color)' }}
              >
                Reschedule Appointment
              </button>
              <button
                type="button"
                onClick={closeAndCancel}
                className="text-sm font-medium text-red-600 hover:text-red-500 focus:outline-none"
              >
                Cancel this appointment instead
              </button>
            </div>
          </>
        )}
      </Modal>
    </>
  );
};

export default ChangeAppointmentButton;
